import { cn } from '@app/utils/cn';
import { handleSpaceAndEnter } from '@app/utils/handleEnterKey';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { loadImage } from '@app/utils/loadImage';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Placeholder } from './Placeholder';

interface Props {
  src?: string;
  aspectRatio: number;
  errorClassName?: string;
  placeholderClassName?: string;
  imageClassName?: string;
  size?: 'auto' | 'cover' | 'contain';
  onClick?: () => void;
  isSourceReady?: boolean;
}

const enum Status {
  Loading,
  Success,
  Error,
  Missing,
}

export const CustomImage = React.memo(
  ({ src, placeholderClassName, aspectRatio, errorClassName, size = 'contain', imageClassName, onClick, isSourceReady = true }: Props) => {
    const { t } = useTranslation(['common']);
    const [source, setSource] = React.useState('');
    const [status, setStatus] = React.useState(Status.Loading);

    React.useEffect(() => {
      if (!isSourceReady) {
        return;
      }

      if (!isDefined(src) || isEmpty(src)) {
        setStatus(Status.Missing);

        return;
      }

      const handleLoad = () => {
        setSource(src);
        setStatus(Status.Success);
        loadImageSubscription.unsubscribe();
      };

      const handleError = () => {
        setStatus(Status.Error);
        loadImageSubscription.unsubscribe();
      };

      const loadImageSubscription = loadImage(src).subscribe({
        complete: handleLoad,
        error: handleError,
      });

      return () => loadImageSubscription.unsubscribe();
    }, [src, isSourceReady]);

    const role = isDefined(onClick) ? 'button' : 'img';

    switch (status) {
      case Status.Loading:
        return <Placeholder className={placeholderClassName} aspectRatio={aspectRatio} shouldShowIcon={false} />;
      case Status.Success: {
        return (
          <Placeholder className={placeholderClassName} aspectRatio={aspectRatio}>
            <div
              role={role}
              className={cn('absolute bottom-0 left-0 right-0 top-0 bg-center bg-no-repeat', imageClassName)}
              style={{
                backgroundImage: `url(${source})`,
                backgroundSize: size,
              }}
              onClick={onClick}
              onKeyDown={handleSpaceAndEnter(onClick)}
            />
          </Placeholder>
        );
      }
      case Status.Error:
      case Status.Missing:
        return (
          <Placeholder className={placeholderClassName} aspectRatio={aspectRatio} role={role} onClick={onClick} shouldShowIcon>
            <div className={errorClassName}>{status === Status.Missing ? t('common:no_image') : t('common:display_image_fail')}</div>
          </Placeholder>
        );
      default:
        throw new Error('Incorrect status.');
    }
  },
);
